const POLL_INTERVAL_MS = 5_000;
// Allow the 180-second Lambda invocation to finish, plus startup/network time.
const RECOVERY_DEADLINE_MS = 210_000;
const TRANSIENT_STATUSES = new Set([408, 429, 502, 503, 504]);

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const recoveryError = (message) => Object.assign(new Error(message), { data: { retryable: false } });
const isTransient = (error) => {
    if (typeof error?.data?.retryable === 'boolean') return error.data.retryable;
    return !error?.status || TRANSIENT_STATUSES.has(Number(error.status));
};

export const getSubmissionFileWithRecovery = async (request, body, { isCancelled = () => false } = {}) => {
    const deadline = Date.now() + RECOVERY_DEADLINE_MS;
    if (isCancelled()) throw recoveryError('Import cancelled');

    try {
        return await request(body, { timeout: 35_000 });
    } catch (error) {
        // An explicit handler failure can use the existing retry flow. A lost
        // response may still have a running upload, so check it before retrying.
        if (
            !body.uploadKey ||
            error?.data?.retryable != null ||
            (error?.status && ![502, 504].includes(Number(error.status)))
        ) {
            throw error;
        }
    }

    while (Date.now() < deadline) {
        if (isCancelled()) throw recoveryError('Import cancelled');
        try {
            const result = await request(
                { ...body, checkOnly: true },
                { timeout: Math.min(10_000, deadline - Date.now()) }
            );
            if (isCancelled()) throw recoveryError('Import cancelled');
            if (result?.status !== 'pending') {
                if (!result?.bucket || !result?.key || !result?.name || !Number.isSafeInteger(result?.size)) {
                    throw recoveryError('Invalid import status response');
                }
                return result;
            }
        } catch (error) {
            if (!isTransient(error)) {
                throw recoveryError(error?.message || error?.data?.error || 'Unable to check import status');
            }
        }
        const remaining = deadline - Date.now();
        if (remaining > 0) await sleep(Math.min(POLL_INTERVAL_MS, remaining));
    }

    // Do not let withRetry restart a transfer whose outcome is still unknown.
    throw recoveryError('The import did not finish in time. Please try again.');
};
